import React, { useEffect, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { getOnboardingStepsForPage } from "../config/onboardingSteps";

function ensureScrollRestored() {
  document.body.classList.remove("driver-active", "driver-fade", "driver-simple");
  document.documentElement.classList.remove("driver-active", "driver-fade", "driver-simple");
  document.body.style.overflow = "";
  document.body.style.pointerEvents = "";
  document.body.style.position = "";
  document.body.style.height = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.width = "";
  document.documentElement.style.overflow = "";
  document.documentElement.style.pointerEvents = "";
  document.documentElement.style.height = "";
  window.scrollTo(0, 0);
  const root = document.getElementById("root");
  if (root) {
    root.style.overflow = "";
    root.style.height = "";
    root.scrollTop = 0;
  }
  document.querySelectorAll(".driver-active-element, .driver-no-interaction").forEach((el) => {
    el.classList.remove("driver-active-element", "driver-no-interaction");
  });
  document.querySelectorAll(".driver-overlay, .driver-overlay-animated, .driver-popover, #driver-popover-content").forEach((el) => {
    try { if (el.parentNode) el.parentNode.removeChild(el); } catch (_) {}
  });
  try {
    const dummy = document.getElementById("driver-dummy-element");
    if (dummy && dummy.parentNode) dummy.parentNode.removeChild(dummy);
  } catch (_) {}
}

/**
 * Onboarding tutorial overlay for first-time free trial users.
 * Runs only on the given page; on complete calls onPhaseComplete(page).
 * Parent is responsible for saving progress and navigating to the next page.
 */
const OnboardingTutorialOverlay = ({ page, run, onPhaseComplete, onClose }) => {
  const driverRef = useRef(null);
  const onPhaseCompleteRef = useRef(onPhaseComplete);
  const onCloseRef = useRef(onClose);
  const closedByUserRef = useRef(false);

  // Keep refs updated so callbacks always use latest handlers
  onPhaseCompleteRef.current = onPhaseComplete;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!run || !page) {
      if (driverRef.current?.isActive?.()) {
        driverRef.current.destroy();
      }
      ensureScrollRestored();
      driverRef.current = null;
      return;
    }

    const steps = getOnboardingStepsForPage(page);
    if (steps.length === 0) {
      onPhaseCompleteRef.current?.(page);
      return;
    }

    closedByUserRef.current = false;

    const driverObj = driver({
      steps,
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      allowClose: true,
      overlayColor: "rgba(0,0,0,0.6)",
      smoothScroll: true,
      stagePadding: 8,
      stageRadius: 8,
      animate: true,
      doneBtnText: "Next →",
      onCloseClick: (element, step, opts) => {
        closedByUserRef.current = true;
        onCloseRef.current?.();
        opts.driver.destroy();
        ensureScrollRestored();
        requestAnimationFrame(() => {
          ensureScrollRestored();
          window.scrollTo(0, 0);
          setTimeout(ensureScrollRestored, 150);
        });
      },
      onDestroyStarted: (element, step, opts) => {
        if (closedByUserRef.current) {
          // Already handled by onCloseClick — just destroy
          opts.driver.destroy();
          ensureScrollRestored();
          return;
        }
        const isLast = opts.driver.isLastStep();
        if (isLast) {
          onPhaseCompleteRef.current?.(page);
        }
        onCloseRef.current?.();
        opts.driver.destroy();
        ensureScrollRestored();
        requestAnimationFrame(() => {
          ensureScrollRestored();
          window.scrollTo(0, 0);
          setTimeout(ensureScrollRestored, 150);
        });
      },
    });

    driverRef.current = driverObj;

    const startTour = () => {
      if (!driverRef.current) return;
      driverObj.drive(0);
    };

    // QR page: longer delay so layout is ready and overlay paints (avoids tour UI not showing).
    // Menu: short delay to avoid race with scroll-fix. Start after paint so overlay is visible.
    const baseDelay = page === "qr" ? 350 : page === "menu" ? 150 : 0;
    const startId = setTimeout(() => {
      requestAnimationFrame(() => startTour());
    }, baseDelay);

    return () => {
      clearTimeout(startId);
      if (driverRef.current?.isActive?.()) {
        driverRef.current.destroy();
      }
      ensureScrollRestored();
      driverRef.current = null;
    };
  }, [run, page]);

  return null;
};

export default OnboardingTutorialOverlay;
